/**
 * TooltipValue - Compact value display with label shown in tooltip
 *
 * For maximum information density, the field label is hidden by default
 * and only appears on hover. Supports optional icons and color variants.
 */

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface TooltipValueProps {
  /** Label shown in tooltip on hover */
  label: string
  /** The value to display */
  value: string | number | null | undefined
  /** Optional icon to show before the value */
  icon?: React.ReactNode
  /** Visual variant for color coding (e.g., expired dates) */
  variant?: 'default' | 'warning' | 'error' | 'success' | 'muted'
  /** Additional CSS class */
  className?: string
  /** Whether to show the value as a badge */
  asBadge?: boolean
}

const variantClasses = {
  default: 'text-foreground',
  warning: 'text-amber-600 dark:text-amber-500',
  error: 'text-red-600 dark:text-red-500',
  success: 'text-green-600 dark:text-green-500',
  muted: 'text-muted-foreground',
}

const badgeVariantClasses = {
  default: 'bg-muted text-muted-foreground',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  muted: 'bg-muted/50 text-muted-foreground',
}

export function TooltipValue({
  label,
  value,
  icon,
  variant = 'default',
  className = '',
  asBadge = false,
}: TooltipValueProps) {
  // Don't render if no value
  if (value === null || value === undefined || value === '') {
    return null
  }

  const displayValue = String(value)

  const content = asBadge ? (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
        badgeVariantClasses[variant],
        className
      )}
    >
      {icon}
      {displayValue}
    </span>
  ) : (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium',
        variantClasses[variant],
        className
      )}
    >
      {icon}
      {displayValue}
    </span>
  )

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          {content}
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * CompactRow - A row layout for tooltip values (label hidden, values aligned)
 */
interface CompactRowProps {
  children: React.ReactNode
  className?: string
}

export function CompactRow({ children, className = '' }: CompactRowProps) {
  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      {children}
    </div>
  )
}
